import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { CrmMerchandiser } from '../crm.model';
import { CrmService } from '../crm.service';
import { ToolbarService } from '../../services/toolbar.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

@Component({
  selector: 'app-crm-merchandiser-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './merchandiser-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MerchandiserFormComponent implements OnInit {
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);

  @Input() embedded = false;
  @Input() dialogCompanyId?: number | null;
  @Input() contextClientName?: string | null;
  @Input() contextAccountName?: string | null;
  @Output() merchandiserSaved = new EventEmitter<CrmMerchandiser>();
  @Output() formCancelled = new EventEmitter<void>();

  merchandiserId = signal<number | null>(null);
  saving = signal(false);
  loading = signal(false);
  submitted = signal(false);

  activeOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/dashboard' },
    { label: 'Merchandisers', routerLink: '/crm/merchandisers' }
  ];

  form = this.fb.group({
    name: ['', Validators.required],
    employeeCode: [''],
    emailAddress: [''],
    phoneNumber: [''],
    mobileNumber: [''],
    region: [''],
    notes: [''],
    active: [true]
  });

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (Number.isFinite(id) && id > 0) {
      this.merchandiserId.set(id);
      this.load(id);
    }
  }

  load(id: number): void {
    this.loading.set(true);
    this.crmService.getMerchandiser(id).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        const row = response.data;
        if (!row) return;
        this.form.patchValue({
          name: row.name || '',
          employeeCode: row.employeeCode || '',
          emailAddress: row.emailAddress || '',
          phoneNumber: row.phoneNumber || '',
          mobileNumber: row.mobileNumber || '',
          region: row.region || '',
          notes: row.notes || '',
          active: row.active !== false
        });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Merchandisers', detail: 'Unable to load merchandiser.' })
    });
  }

  save(): void {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }
    const raw = this.form.getRawValue();
    const payload: CrmMerchandiser = {
      id: this.merchandiserId() || undefined,
      name: raw.name?.trim() || '',
      employeeCode: raw.employeeCode?.trim() || undefined,
      emailAddress: raw.emailAddress?.trim() || undefined,
      phoneNumber: raw.phoneNumber?.trim() || undefined,
      mobileNumber: raw.mobileNumber?.trim() || undefined,
      region: raw.region?.trim() || undefined,
      notes: raw.notes?.trim() || undefined,
      active: raw.active !== false,
      companyId: this.companyId() || 0
    };
    const request = payload.id ? this.crmService.updateMerchandiser(payload) : this.crmService.addMerchandiser(payload);
    this.saving.set(true);
    request.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: response => {
        if (response.success) {
          const id = response.data?.id || payload.id;
          const saved = response.data || { ...payload, id };
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Merchandiser saved.' });
          if (this.embedded) {
            this.merchandiserSaved.emit(saved);
            return;
          }
          this.router.navigate(id ? ['/crm/merchandisers', id] : ['/crm/merchandisers']);
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save merchandiser.' })
    });
  }

  cancel(): void {
    if (this.embedded) {
      this.formCancelled.emit();
      return;
    }
    const id = this.merchandiserId();
    this.router.navigate(id ? ['/crm/merchandisers', id] : ['/crm/merchandisers']);
  }

  private companyId(): number | undefined {
    return this.dialogCompanyId ?? this.toolbarService.customer()?.company?.id;
  }
}
